import React, { createContext, useContext, ReactNode } from "react";
import { useOrganization } from "~hooks";

interface StripeConfigContextType {
  integrationId?: string;
  publishableKey?: string;
  connectedAccountId?: string;
  expressCheckoutEnabled?: boolean;
}

const StripeConfigContext = createContext<StripeConfigContextType | undefined>(
  undefined
);

interface StripeProviderProps extends StripeConfigContextType {
  children: ReactNode;
  config?: StripeConfigContextType;
}

export const StripeConfigProvider: React.FC<StripeProviderProps> = ({
  config = {},
  children,
}) => {
  const { hasFeature } = useOrganization();

  // Legacy stripe accounts are used for internal demoing purposes only via "legacyStripeExpressCheckout"
  const allowStripeAccount =
    !!config?.connectedAccountId || hasFeature("stripeExpressCheckoutLegacy");

  return (
    <StripeConfigContext.Provider
      value={{
        ...config,
        // Express checkout is only enabled for customers if the organization has a Stripe connect account
        expressCheckoutEnabled:
          hasFeature("stripeExpressCheckout") &&
          allowStripeAccount &&
          !!config?.publishableKey,
      }}
    >
      {children}
    </StripeConfigContext.Provider>
  );
};

export const useStripeConfigContext = () => {
  const context = useContext(StripeConfigContext);
  if (!context) {
    console.error(
      "useStripeConfigContext must be used within a StripeProvider"
    );
  }
  return context;
};
